import React, { useState } from "react";
import { Loader2, Layers } from "lucide-react";
import { toast } from "sonner";
import API, { formatApiError } from "../../lib/api";
import { fmtEUR } from "../../lib/invoiceConstants";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";

const today = () => new Date().toISOString().slice(0, 10);

const GroupedInvoiceManager = ({ open, onOpenChange, clients = [], onCreated }) => {
  const [clientId, setClientId] = useState("");
  const [items, setItems] = useState([]); // billable
  const [sel, setSel] = useState({}); // {interventionId: {checked, amountHT, designation}}
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [meta, setMeta] = useState({ issueDate: today(), periodFrom: "", periodTo: "" });

  const loadBillable = async (cid) => {
    setClientId(cid);
    setItems([]);
    setSel({});
    if (!cid) return;
    setLoading(true);
    try {
      const { data } = await API.get("/billable", { params: { clientId: cid } });
      setItems(data.items);
      const init = {};
      data.items.forEach((it) => {
        init[it.interventionId] = { checked: false, amountHT: "", designation: it.suggestedDesignation };
      });
      setSel(init);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const upd = (id, patch) => setSel((s) => ({ ...s, [id]: { ...s[id], ...patch } }));

  const selectedLines = items
    .filter((it) => sel[it.interventionId]?.checked)
    .map((it) => ({
      interventionId: it.interventionId,
      designation: sel[it.interventionId].designation,
      amountHT: Number(sel[it.interventionId].amountHT) || 0,
    }));

  const total = selectedLines.reduce((s, l) => s + l.amountHT, 0);

  const create = async () => {
    if (selectedLines.length === 0) { toast.error("Sélectionnez au moins une intervention."); return; }
    if (selectedLines.some((l) => !l.designation || l.amountHT <= 0)) { toast.error("Renseignez un libellé et un montant > 0 pour chaque ligne."); return; }
    const client = clients.find((c) => c.id === clientId);
    setCreating(true);
    try {
      const { data } = await API.post("/grouped-invoices", {
        clientId,
        buyer: { name: client?.company || client?.name || "Client", email: client?.email || null, isCompany: client?.role === "agence" },
        issueDate: meta.issueDate,
        periodFrom: meta.periodFrom || null,
        periodTo: meta.periodTo || null,
        lines: selectedLines,
      });
      toast.success(`Facture groupée créée (brouillon) — ${data.lines.length} interventions`);
      setClientId(""); setItems([]); setSel({});
      onOpenChange(false);
      onCreated?.(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="grouped-manager-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Layers className="w-5 h-5" /> Nouvelle facture groupée</DialogTitle>
          <DialogDescription>Sélectionnez un client puis les interventions terminées à regrouper en une seule facture.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="space-y-1 sm:col-span-1">
              <Label className="text-xs">Client / Agence</Label>
              <Select value={clientId} onValueChange={loadBillable}>
                <SelectTrigger data-testid="grouped-client-select"><SelectValue placeholder="Choisir" /></SelectTrigger>
                <SelectContent>
                  {clients.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name} {c.company ? `— ${c.company}` : ""}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label className="text-xs">Période du</Label><Input type="date" value={meta.periodFrom} onChange={(e) => setMeta((m) => ({ ...m, periodFrom: e.target.value }))} /></div>
            <div className="space-y-1"><Label className="text-xs">au</Label><Input type="date" value={meta.periodTo} onChange={(e) => setMeta((m) => ({ ...m, periodTo: e.target.value }))} /></div>
          </div>

          {loading ? (
            <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[hsl(207,78%,42%)]" /></div>
          ) : clientId && items.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-6">Aucune intervention facturable pour ce client (interventions terminées non encore facturées).</p>
          ) : items.length > 0 ? (
            <div className="border rounded-lg divide-y max-h-[45vh] overflow-y-auto">
              {items.map((it) => {
                const s = sel[it.interventionId] || {};
                return (
                  <div key={it.interventionId} className="p-3 flex flex-col sm:flex-row sm:items-center gap-2" data-testid={`billable-${it.dossierNumber}`}>
                    <div className="flex items-start gap-2 flex-1">
                      <Checkbox checked={!!s.checked} onCheckedChange={(v) => upd(it.interventionId, { checked: !!v })} data-testid={`billable-check-${it.dossierNumber}`} />
                      <div className="text-xs">
                        <p className="font-mono font-semibold text-[hsl(207,78%,42%)]">{it.bonNumber || it.dossierNumber}</p>
                        <p className="text-muted-foreground">{it.date} · {it.address}</p>
                      </div>
                    </div>
                    <Input className="sm:w-64 text-xs" value={s.designation || ""} onChange={(e) => upd(it.interventionId, { designation: e.target.value })} placeholder="Libellé" />
                    <Input className="sm:w-28 text-xs" type="number" min="0" step="0.01" value={s.amountHT || ""} onChange={(e) => upd(it.interventionId, { amountHT: e.target.value })} placeholder="Montant HT" data-testid={`billable-amount-${it.dossierNumber}`} />
                  </div>
                );
              })}
            </div>
          ) : null}

          <div className="flex items-center justify-between">
            <p className="text-sm">Total sélection : <strong className="text-[hsl(207,78%,42%)]" data-testid="grouped-total">{fmtEUR(total)}</strong> <span className="text-xs text-muted-foreground">({selectedLines.length} interv.)</span></p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
              <Button className="btn-primary" onClick={create} disabled={creating || selectedLines.length === 0} data-testid="grouped-create-button">
                {creating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Créer la facture groupée
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GroupedInvoiceManager;
